import {
  typeOwner,
  type Id,
  type LinkRecord,
  type ObjectRecord,
  type Op,
  type Source,
  type TableName,
  type Transaction,
} from '@vp/shared-types';
import { boxBounds, CoordinateLimitError, withinLimits } from './geometry';
import { ulid } from './ulid';

export class ReadOnlyError extends Error {
  readonly code = 'E_READ_ONLY';
  constructor() {
    super('Canvas is open in read-only mode');
  }
}

export class CycleError extends Error {
  readonly code = 'E_CYCLE';
  constructor(from: Id, to: Id) {
    super(`Link ${from} → ${to} would create a dependency cycle`);
  }
}

export class TxError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export interface ChangeEvent {
  created: Id[];
  updated: Id[];
  deleted: Id[];
  links: Id[];
  origin: 'local' | 'load' | 'external';
}

export interface TxOptions {
  source?: Source;
  label?: string;
  undoOf?: Id;
  redoOf?: Id;
}

export interface CommitMeta {
  /** Applied by undo/redo — history must not record it as a new step. */
  history?: boolean;
  /** Replayed from the WAL — persist state but do not journal again. */
  replay?: boolean;
}

type Rec = Record<string, unknown> & { id: Id };

const GEOMETRY_KEYS = ['x', 'y', 'w', 'h', 'rotation'];

/**
 * In-memory canvas document. All mutations go through transactions (§5.3, §12.2).
 * Records are treated as immutable: every update replaces the record object.
 */
export class CanvasDocument {
  readonly canvasId: Id;
  readOnly = false;

  private readonly objects = new Map<Id, ObjectRecord>();
  private readonly links = new Map<Id, LinkRecord>();
  private readonly linksFrom = new Map<Id, Set<Id>>();
  private readonly linksTo = new Map<Id, Set<Id>>();
  private readonly children = new Map<Id, Set<Id>>();
  private readonly members = new Map<Id, Set<Id>>();
  private readonly revs = new Map<Id, number>();
  private revCounter = 0;

  private pending: { created: Set<Id>; updated: Set<Id>; deleted: Set<Id>; links: Set<Id>; origin: ChangeEvent['origin'] } | null = null;
  private flushScheduled = false;
  private changeListeners = new Set<(e: ChangeEvent) => void>();
  private commitListeners = new Set<(tx: Transaction, meta: CommitMeta) => void>();
  private active: TxContext | null = null;

  constructor(canvasId: Id) {
    this.canvasId = canvasId;
  }

  // ---------- reads ----------
  get(id: Id): ObjectRecord | undefined {
    return this.objects.get(id);
  }
  has(id: Id): boolean {
    return this.objects.has(id);
  }
  get size(): number {
    return this.objects.size;
  }
  allObjects(): IterableIterator<ObjectRecord> {
    return this.objects.values();
  }
  objectIds(): IterableIterator<Id> {
    return this.objects.keys();
  }
  getLink(id: Id): LinkRecord | undefined {
    return this.links.get(id);
  }
  allLinks(): IterableIterator<LinkRecord> {
    return this.links.values();
  }
  linksOf(objectId: Id): LinkRecord[] {
    const out: LinkRecord[] = [];
    for (const id of this.linksFrom.get(objectId) ?? []) out.push(this.links.get(id)!);
    for (const id of this.linksTo.get(objectId) ?? []) out.push(this.links.get(id)!);
    return out;
  }
  incomingLinks(objectId: Id): LinkRecord[] {
    return [...(this.linksTo.get(objectId) ?? [])].map((id) => this.links.get(id)!);
  }
  outgoingLinks(objectId: Id): LinkRecord[] {
    return [...(this.linksFrom.get(objectId) ?? [])].map((id) => this.links.get(id)!);
  }
  /** Direct contents of a frame. */
  membersOf(frameId: Id): Id[] {
    return [...(this.members.get(frameId) ?? [])];
  }
  childrenOf(parentId: Id): Id[] {
    return [...(this.children.get(parentId) ?? [])];
  }
  /** Monotonic revision of a record; changes on every mutation. */
  rev(id: Id): number {
    return this.revs.get(id) ?? 0;
  }
  get inTransaction(): boolean {
    return this.active !== null;
  }

  /** Would a link from → to close a dependency cycle? (§8.3) */
  wouldCreateCycle(from: Id, to: Id): boolean {
    if (from === to) return true;
    const stack = [to];
    const seen = new Set<Id>();
    while (stack.length) {
      const cur = stack.pop()!;
      if (cur === from) return true;
      if (seen.has(cur)) continue;
      seen.add(cur);
      for (const lid of this.linksFrom.get(cur) ?? []) stack.push(this.links.get(lid)!.to);
    }
    return false;
  }

  // ---------- events ----------
  onChange(fn: (e: ChangeEvent) => void): () => void {
    this.changeListeners.add(fn);
    return () => this.changeListeners.delete(fn);
  }
  onCommit(fn: (tx: Transaction, meta: CommitMeta) => void): () => void {
    this.commitListeners.add(fn);
    return () => this.commitListeners.delete(fn);
  }

  // ---------- transactions ----------
  begin(opts: TxOptions = {}): TxContext {
    if (this.readOnly) throw new ReadOnlyError();
    if (this.active) throw new TxError('E_TX_NESTED', 'A transaction is already open');
    this.active = new TxContext(this, opts);
    return this.active;
  }

  /** Run `fn` in a transaction; commits on success, rolls back on error. */
  transact<T>(fn: (tx: TxContext) => T, opts: TxOptions = {}): { tx: Transaction | null; result: T } {
    const ctx = this.begin(opts);
    try {
      const result = fn(ctx);
      const tx = ctx.commit();
      return { tx, result };
    } catch (e) {
      if (ctx.isOpen) ctx.rollback();
      throw e;
    }
  }

  /** Bulk load without history (initial load / import / external refresh). */
  load(objects: Iterable<ObjectRecord>, links: Iterable<LinkRecord> = [], origin: ChangeEvent['origin'] = 'load'): void {
    for (const o of objects) this.rawPut('objects', o as Rec, origin);
    for (const l of links) this.rawPut('links', l as Rec, origin);
    this.flush();
  }

  /** Replace the whole content (used by read-only tabs to refresh). */
  replaceAll(objects: Iterable<ObjectRecord>, links: Iterable<LinkRecord>): void {
    for (const id of [...this.objects.keys()]) this.rawDelete('objects', id, 'external');
    for (const id of [...this.links.keys()]) this.rawDelete('links', id, 'external');
    this.load(objects, links, 'external');
  }

  /** Apply ops of a foreign transaction (e.g. undo inverse) as a new committed transaction. */
  applyOps(ops: Op[], opts: TxOptions, meta: CommitMeta = {}): Transaction | null {
    const ctx = this.begin(opts);
    try {
      // Links reference objects: drop links first, restore them last.
      const rank = (op: Op): number => (op.table === 'links' ? (op.t === 'delete' ? 0 : 2) : 1);
      const ordered = ops.map((op, i) => ({ op, i })).sort((a, b) => rank(a.op) - rank(b.op) || a.i - b.i);
      for (const { op } of ordered) ctx.applyOp(op);
      return ctx.commit(meta);
    } catch (e) {
      if (ctx.isOpen) ctx.rollback();
      throw e;
    }
  }

  // ---------- internals used by TxContext ----------
  /** @internal */
  _endTx(): void {
    this.active = null;
  }

  /** @internal */
  _emitCommit(tx: Transaction, meta: CommitMeta): void {
    this.flush();
    for (const fn of this.commitListeners) fn(tx, meta);
  }

  /** @internal */
  /** Highest layer key; undefined — not known (recomputed on demand). */
  private topZCache: string | null | undefined = undefined;

  /** The highest layer key of all objects, or null on an empty canvas. Kept up to date, so creating many objects stays linear. */
  topZ(): string | null {
    if (this.topZCache === undefined) {
      let top: string | null = null;
      for (const o of this.objects.values()) if (top === null || o.z > top) top = o.z;
      this.topZCache = top;
    }
    return this.topZCache;
  }

  rawGet(table: TableName, id: Id): Rec | undefined {
    return (table === 'objects' ? this.objects.get(id) : this.links.get(id)) as Rec | undefined;
  }

  /** @internal */
  rawPut(table: TableName, rec: Rec, origin: ChangeEvent['origin'] = 'local'): void {
    if (table === 'objects') {
      const o = rec as unknown as ObjectRecord;
      const prev = this.objects.get(o.id);
      if (prev && prev.parentId !== o.parentId && prev.parentId) this.children.get(prev.parentId)?.delete(o.id);
      if (prev && prev.frameId !== o.frameId && prev.frameId) this.members.get(prev.frameId)?.delete(o.id);
      if (o.frameId) {
        let ms = this.members.get(o.frameId);
        if (!ms) this.members.set(o.frameId, (ms = new Set()));
        ms.add(o.id);
      }
      if (o.parentId) {
        let set = this.children.get(o.parentId);
        if (!set) this.children.set(o.parentId, (set = new Set()));
        set.add(o.id);
      }
      if (this.topZCache !== undefined) {
        // a raised key may become the top; the top lowered must be found again
        if (prev && prev.z === this.topZCache && o.z < prev.z) this.topZCache = undefined;
        else if (this.topZCache === null || o.z > this.topZCache) this.topZCache = o.z;
      }
      this.objects.set(o.id, o);
      this.revs.set(o.id, ++this.revCounter);
      this.mark(prev ? 'updated' : 'created', o.id, origin);
    } else {
      const l = rec as unknown as LinkRecord;
      const prev = this.links.get(l.id);
      if (prev) this.unindexLink(prev);
      this.links.set(l.id, l);
      this.indexLink(l);
      this.mark('links', l.id, origin);
    }
  }

  /** @internal */
  rawDelete(table: TableName, id: Id, origin: ChangeEvent['origin'] = 'local'): void {
    if (table === 'objects') {
      const prev = this.objects.get(id);
      if (!prev) return;
      if (prev.parentId) this.children.get(prev.parentId)?.delete(id);
      if (prev.frameId) this.members.get(prev.frameId)?.delete(id);
      if (prev.z === this.topZCache) this.topZCache = undefined;
      this.objects.delete(id);
      this.revs.set(id, ++this.revCounter);
      this.mark('deleted', id, origin);
    } else {
      const prev = this.links.get(id);
      if (!prev) return;
      this.unindexLink(prev);
      this.links.delete(id);
      this.mark('links', id, origin);
    }
  }

  private indexLink(l: LinkRecord): void {
    let f = this.linksFrom.get(l.from);
    if (!f) this.linksFrom.set(l.from, (f = new Set()));
    f.add(l.id);
    let t = this.linksTo.get(l.to);
    if (!t) this.linksTo.set(l.to, (t = new Set()));
    t.add(l.id);
  }

  private unindexLink(l: LinkRecord): void {
    this.linksFrom.get(l.from)?.delete(l.id);
    this.linksTo.get(l.to)?.delete(l.id);
  }

  private mark(kind: 'created' | 'updated' | 'deleted' | 'links', id: Id, origin: ChangeEvent['origin']): void {
    if (!this.pending) {
      this.pending = { created: new Set(), updated: new Set(), deleted: new Set(), links: new Set(), origin };
    }
    const p = this.pending;
    if (kind === 'links') p.links.add(id);
    else if (kind === 'created') {
      p.deleted.delete(id);
      p.created.add(id);
    } else if (kind === 'updated') {
      if (!p.created.has(id)) p.updated.add(id);
    } else {
      if (p.created.has(id)) p.created.delete(id);
      else p.deleted.add(id);
      p.updated.delete(id);
    }
    if (!this.flushScheduled) {
      this.flushScheduled = true;
      queueMicrotask(() => this.flush());
    }
  }

  /** Emit accumulated change notifications synchronously. */
  flush(): void {
    this.flushScheduled = false;
    const p = this.pending;
    if (!p) return;
    this.pending = null;
    const e: ChangeEvent = {
      created: [...p.created],
      updated: [...p.updated],
      deleted: [...p.deleted],
      links: [...p.links],
      origin: p.origin,
    };
    for (const fn of this.changeListeners) fn(e);
  }
}

function pick(rec: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = rec[k];
  return out;
}

function applyPatch(rec: Rec, patch: Record<string, unknown>): Rec {
  const next: Rec = { ...rec };
  for (const k of Object.keys(patch)) {
    if (k === 'id') continue;
    const v = patch[k];
    if (v === undefined) delete next[k];
    else next[k] = v;
  }
  return next;
}

/**
 * Open transaction. Changes apply immediately (live preview) and are
 * recorded as ops; repeated updates of one record are coalesced.
 */
export class TxContext {
  private readonly doc: CanvasDocument;
  private readonly opts: TxOptions;
  private readonly ops = new Map<string, Op>();
  private open = true;
  /** Inside `applyOp`: records come back as they were (undo, redo, replay), nothing is dropped. */
  private replaying = false;

  constructor(doc: CanvasDocument, opts: TxOptions) {
    this.doc = doc;
    this.opts = opts;
  }

  get isOpen(): boolean {
    return this.open;
  }
  get document(): CanvasDocument {
    return this.doc;
  }
  get opCount(): number {
    return this.ops.size;
  }

  get(id: Id): ObjectRecord | undefined {
    return this.doc.get(id);
  }

  private ensureOpen(): void {
    if (!this.open) throw new TxError('E_TX_CLOSED', 'Transaction is closed');
  }

  /** A direct edit of the user: objects it creates or changes stop being a module's own (API 4.0). */
  private get byUser(): boolean {
    return !this.replaying && (this.opts.source?.kind ?? 'user') === 'user';
  }

  private checkGeometry(rec: Rec): void {
    const o = rec as unknown as ObjectRecord;
    if (!withinLimits(boxBounds(o))) throw new CoordinateLimitError();
  }

  create(rec: ObjectRecord): ObjectRecord {
    this.ensureOpen();
    if (rec.canvasId !== this.doc.canvasId) throw new TxError('E_CANVAS', 'Record belongs to another canvas');
    if (this.doc.has(rec.id)) throw new TxError('E_EXISTS', `Object ${rec.id} already exists`);
    if (this.byUser && rec.createdByModule !== undefined) {
      const { createdByModule: _, ...rest } = rec;
      rec = rest as ObjectRecord;
    }
    this.checkGeometry(rec as Rec);
    this.record({ t: 'create', table: 'objects', rec: rec as Rec });
    this.doc.rawPut('objects', rec as Rec);
    return rec;
  }

  update(id: Id, patch: Partial<ObjectRecord> & Record<string, unknown>): ObjectRecord {
    this.ensureOpen();
    const cur = this.doc.rawGet('objects', id);
    if (!cur) throw new TxError('E_NOT_FOUND', `Object ${id} not found`);
    if (this.byUser && cur['createdByModule'] !== undefined) patch = { ...patch, createdByModule: undefined };
    const keys = Object.keys(patch).filter((k) => k !== 'id' && k !== 'canvasId');
    const next = applyPatch(cur, pick(patch, keys));
    if (keys.some((k) => GEOMETRY_KEYS.includes(k))) this.checkGeometry(next);
    this.recordUpdate('objects', id, pick(cur, keys), pick(next, keys));
    this.doc.rawPut('objects', next);
    return next as unknown as ObjectRecord;
  }

  /** Shallow-merge into `props`. */
  updateProps(id: Id, props: Record<string, unknown>): ObjectRecord {
    const cur = this.doc.get(id);
    if (!cur) throw new TxError('E_NOT_FOUND', `Object ${id} not found`);
    return this.update(id, { props: { ...cur.props, ...props } });
  }

  /** Shallow-merge into `style`. */
  updateStyle(id: Id, style: Record<string, unknown>): ObjectRecord {
    const cur = this.doc.get(id);
    if (!cur) throw new TxError('E_NOT_FOUND', `Object ${id} not found`);
    return this.update(id, { style: { ...cur.style, ...style } as ObjectRecord['style'] });
  }

  /** Deletes an object together with its links (§8.4: undo restores both). */
  delete(id: Id): void {
    this.ensureOpen();
    const cur = this.doc.rawGet('objects', id);
    if (!cur) return;
    for (const l of this.doc.linksOf(id)) this.deleteLink(l.id);
    this.record({ t: 'delete', table: 'objects', rec: cur });
    this.doc.rawDelete('objects', id);
  }

  createLink(link: LinkRecord): LinkRecord {
    this.ensureOpen();
    if (this.doc.getLink(link.id)) throw new TxError('E_EXISTS', `Link ${link.id} already exists`);
    if (!this.doc.has(link.from) || !this.doc.has(link.to)) throw new TxError('E_NOT_FOUND', 'Link endpoint not found');
    if (this.doc.wouldCreateCycle(link.from, link.to)) throw new CycleError(link.from, link.to);
    this.record({ t: 'create', table: 'links', rec: link as Rec });
    this.doc.rawPut('links', link as Rec);
    return link;
  }

  updateLink(id: Id, patch: Partial<LinkRecord>): LinkRecord {
    this.ensureOpen();
    const cur = this.doc.rawGet('links', id);
    if (!cur) throw new TxError('E_NOT_FOUND', `Link ${id} not found`);
    const keys = Object.keys(patch).filter((k) => k !== 'id' && k !== 'from' && k !== 'to' && k !== 'canvasId');
    const next = applyPatch(cur, pick(patch as Record<string, unknown>, keys));
    this.recordUpdate('links', id, pick(cur, keys), pick(next, keys));
    this.doc.rawPut('links', next);
    return next as unknown as LinkRecord;
  }

  deleteLink(id: Id): void {
    this.ensureOpen();
    const cur = this.doc.rawGet('links', id);
    if (!cur) return;
    this.record({ t: 'delete', table: 'links', rec: cur });
    this.doc.rawDelete('links', id);
  }

  /** Apply a serialized op (used by undo/redo and imports). Lenient on missing records. */
  applyOp(op: Op): void {
    this.replaying = true;
    try {
      this.applyOpRaw(op);
    } finally {
      this.replaying = false;
    }
  }

  private applyOpRaw(op: Op): void {
    if (op.t === 'create') {
      if (this.doc.rawGet(op.table, op.rec.id)) return;
      if (op.table === 'objects') this.create(op.rec as unknown as ObjectRecord);
      else {
        // Links restored by undo skip cycle validation only if endpoints exist.
        const l = op.rec as unknown as LinkRecord;
        if (!this.doc.has(l.from) || !this.doc.has(l.to)) return;
        this.createLink(l);
      }
    } else if (op.t === 'delete') {
      if (op.table === 'objects') this.delete(op.rec.id);
      else this.deleteLink(op.rec.id);
    } else {
      if (!this.doc.rawGet(op.table, op.id)) return;
      if (op.table === 'objects') this.update(op.id, op.after as Partial<ObjectRecord>);
      else this.updateLink(op.id, op.after as Partial<LinkRecord>);
    }
  }

  private key(table: TableName, id: Id): string {
    return table[0] + id;
  }

  private record(op: Op): void {
    const id = op.t === 'update' ? op.id : op.rec.id;
    const k = this.key(op.table, id);
    const prev = this.ops.get(k);
    if (!prev) {
      this.ops.set(k, op);
      return;
    }
    if (op.t === 'delete') {
      if (prev.t === 'create') {
        this.ops.delete(k); // created and deleted within one tx: net nothing
      } else if (prev.t === 'update') {
        // restore the pre-transaction record
        this.ops.set(k, { t: 'delete', table: op.table, rec: applyPatch(op.rec, prev.before) });
      }
      return;
    }
    if (op.t === 'create' && prev.t === 'delete') {
      // deleted then re-created: becomes an update of the whole record
      const before = prev.rec;
      const after = op.rec;
      const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((x) => x !== 'id');
      this.ops.set(k, { t: 'update', table: op.table, id, before: pick(before, keys), after: pick(after, keys) });
      return;
    }
    this.ops.set(k, op);
  }

  private recordUpdate(table: TableName, id: Id, before: Record<string, unknown>, after: Record<string, unknown>): void {
    const k = this.key(table, id);
    const prev = this.ops.get(k);
    if (!prev) {
      this.ops.set(k, { t: 'update', table, id, before, after });
    } else if (prev.t === 'create') {
      prev.rec = applyPatch(prev.rec, after);
    } else if (prev.t === 'update') {
      for (const key of Object.keys(before)) if (!(key in prev.before)) prev.before[key] = before[key];
      Object.assign(prev.after, after);
    } else {
      throw new TxError('E_TX_STATE', `Record ${id} was deleted in this transaction`);
    }
  }

  private modulesTouched(ops: Op[]): string[] {
    const set = new Set<string>();
    for (const op of ops) {
      let type: unknown;
      if (op.t === 'update') type = (this.doc.rawGet(op.table, op.id) ?? op.before)['type'];
      else type = op.rec['type'];
      if (typeof type === 'string') {
        const owner = typeOwner(type);
        if (owner) set.add(owner);
      }
    }
    return [...set];
  }

  /** Commit; returns `null` when nothing changed. */
  commit(meta: CommitMeta = {}): Transaction | null {
    this.ensureOpen();
    this.open = false;
    this.doc._endTx();
    const ops = [...this.ops.values()];
    if (ops.length === 0) {
      this.doc.flush();
      return null;
    }
    const tx: Transaction = {
      id: ulid(),
      canvasId: this.doc.canvasId,
      ts: Date.now(),
      source: this.opts.source ?? { kind: 'user' },
      ops,
      modules: this.modulesTouched(ops),
      ...(this.opts.label ? { label: this.opts.label } : {}),
      ...(this.opts.undoOf ? { undoOf: this.opts.undoOf } : {}),
      ...(this.opts.redoOf ? { redoOf: this.opts.redoOf } : {}),
    };
    this.doc._emitCommit(tx, meta);
    return tx;
  }

  /** Revert every change made in this transaction. */
  rollback(): void {
    this.ensureOpen();
    this.open = false;
    const ops = [...this.ops.values()].reverse();
    for (const op of ops) revertOp(this.doc, op);
    this.doc._endTx();
    this.doc.flush();
  }
}

function revertOp(doc: CanvasDocument, op: Op): void {
  if (op.t === 'create') doc.rawDelete(op.table, op.rec.id);
  else if (op.t === 'delete') doc.rawPut(op.table, op.rec);
  else {
    const cur = doc.rawGet(op.table, op.id);
    if (cur) doc.rawPut(op.table, applyPatch(cur, op.before));
  }
}

/** Inverse ops (reverse order) for undo. */
export function invertOps(ops: Op[]): Op[] {
  const out: Op[] = [];
  for (let i = ops.length - 1; i >= 0; i--) {
    const op = ops[i];
    if (op.t === 'create') out.push({ t: 'delete', table: op.table, rec: op.rec });
    else if (op.t === 'delete') out.push({ t: 'create', table: op.table, rec: op.rec });
    else out.push({ t: 'update', table: op.table, id: op.id, before: op.after, after: op.before });
  }
  return out;
}
